<template>
  <input 
    @change="handleChange"
    type="number" 
    ref="input" 
    v-bind="inputAttrs" 
    :value="unrectifiedValue"
    @input="handleInput"
    @keyup="$emit('keyup', $event )"
    tabindex="0"
    @focusout="$emit('focusout')"
    @focus="$emit('focus')"
    @keydown="filterValues">
</template>

<script lang="ts">
  import Vue from "vue";
  import {NumberType} from "../enums/numberType";
  import {NumberHelper} from "../helpers/numberHelper";
  import {Hash} from "../interfaces/hashInterfaces";

  export default Vue.extend({
    props: {
      numType: {
        type: Number,
        default: NumberType.integer
      },
      step: {
        type: String,
        default: null
      },
      min: {
        type: Number,
        default: 0
      },
      max: {
        type: Number,
        default: null
      },
      value: {
        type: Number,
        default: null
      },
      replacementValue: {
        type: Number,
        default: null
      },
      allowNegatives: {
        type: Boolean,
        default: false
      },
      updateOnChange: {
        type: Boolean,
        default: false
      },
      preventSpecialCharacters: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        unrectifiedValue: this.value,
      }
    },
    watch: {
      value: function (newVal, oldVal) {
        this.unrectifiedValue = newVal
      },
      min: function (newVal, oldVal) {
        if (newVal > this.value) {
          this.$emit('input', newVal);
        }
      },
      max: function (newVal, oldVal) {
        if (newVal < this.value) {
          this.$emit('input', newVal);
        }
      },
    },
    computed: {
      inputAttrs(): Hash<number> {
        const attrs: Hash<number> = {};
        if (!isNaN(NumberHelper.toIntOrNull(this.max))) {
          attrs.max = this.max;
        }
        if (!this.allowNegatives) {
          attrs.min = this.min;
        }
        if (this.step) {
          attrs.step = this.step;
        }
        return attrs;
      }
    },
    methods: {
      handleChange(e) {
        if (this.updateOnChange) {
          const inputContext = e.target.value === "" ? "keepOldValue" : "replaceValue"
          const rectified = this.validateInput(e.target.value, inputContext); // this is bad
          this.unrectifiedValue = rectified;
          this.$refs.input.value = this.unrectifiedValue;
          this.$emit('change', rectified);
          return;
        }
        this.$emit("change", e);
      },
      handleInput(e): void {
        // if updateOnChange is set to true, then we wait until the change event to update
        if (this.updateOnChange) {
          if (isNaN(this.$refs.input.value)) {
            this.$refs.input.value = this.replacementValue;
          }
          return;
        }
        const rectified = this.validateInput(e.target.value, e.inputType);
        this.$emit('input', rectified);
        this.unrectifiedValue = rectified;
        this.$refs.input.value = this.unrectifiedValue;
      },
      validateInput(value: string, inputType: string): number {
        let input = parseFloat(value);

        if (!this.allowNegatives) {
          if (this.numType === NumberType.float) {
            input = parseFloat(value)
          } else {
            input = parseInt(value, 10);
          }
          if (Number.isNaN(input) || input < this.min) {
            if (inputType === "deleteContentBackward" || inputType === "replaceValue") {
              input = this.replacementValue;
            } else {
              input = this.unrectifiedValue;
            }
          }
          const hasAMax = this.max || this.max === 0;
          if (hasAMax && input > this.max) {
            input = this.max;
          }
        } else {
          if (Number.isNaN(input)) {
            if (inputType === "deleteContentBackward") {
              input = this.replacementValue;
            } else {
              input = this.unrectifiedValue;
            }
          }
        }
        return input;
      },
      filterValues(e): void {
        if (this.preventSpecialCharacters) {
          const invalidCharacters = ["e", "E", "+", "-", "."]
          if (invalidCharacters.includes(e.key)) {
            e.preventDefault();
          }
        }
      }
    }
  });
</script>
